'use client'

import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Underline } from '@tiptap/extension-underline'
import { TextAlign } from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Highlight } from '@tiptap/extension-highlight'
import { Link } from '@tiptap/extension-link'
import { ResizableImage } from '@/components/tiptap/resizable-image'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { useEffect, useRef, useState } from 'react'
import {
    MdFormatBold,
    MdFormatItalic,
    MdFormatUnderlined,
    MdFormatListBulleted,
    MdFormatListNumbered,
    MdFormatAlignLeft,
    MdFormatAlignCenter,
    MdFormatAlignRight,
    MdFormatAlignJustify,
    MdUndo,
    MdRedo,
    MdFormatClear,
    MdTableChart,
    MdLink,
    MdImage,
} from 'react-icons/md'

interface RichTextEditorProps {
    content: string
    onChange: (content: string) => void
    placeholder?: string
    minHeight?: string
    // Quando informado, o botão de imagem faz upload de um arquivo e insere a URL
    // retornada. Sem essa prop, mantém o comportamento antigo (inserir via URL).
    onImageUpload?: (file: File) => Promise<string>
    /**
     * Variáveis oferecidas numa barra extra, inseridas NA POSIÇÃO DO CURSOR.
     *
     * Existe para os modelos de laudo e atestado (`Template`), onde o médico escreve o
     * texto uma vez com `{{paciente}}` e `{{data}}` no meio das frases. Digitar as
     * chaves à mão erra — `{{pasciente}}` só aparece como um buraco no documento
     * impresso, quando já foi entregue.
     *
     * Sem a prop, a barra não aparece e o editor é o de sempre.
     */
    variables?: { key: string; label: string; hint: string }[]
}

export default function RichTextEditor({
    content,
    onChange,
    placeholder = 'Digite o conteúdo...',
    minHeight = '400px',
    onImageUpload,
    variables,
}: RichTextEditorProps) {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [uploadingImage, setUploadingImage] = useState(false)
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-secondary underline cursor-pointer',
                },
            }),
            ResizableImage.configure({
                inline: false,
                allowBase64: false,
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: content,
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none p-4',
                'data-placeholder': placeholder,
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML()
            onChange(html)
        },
    })

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content)
        }
    }, [content, editor])

    if (!editor) {
        return null
    }

    const addLink = () => {
        const url = window.prompt('URL do link:')
        if (url) {
            editor.chain().focus().setLink({ href: url }).run()
        }
    }

    const addImage = () => {
        if (onImageUpload) {
            fileInputRef.current?.click()
            return
        }
        const url = window.prompt('URL da imagem:')
        if (url) {
            editor.chain().focus().setImage({ src: url }).run()
        }
    }

    const handleImageFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0]
        // limpa o input para permitir reenviar o mesmo arquivo depois
        event.target.value = ''
        if (!file || !onImageUpload) return

        setUploadingImage(true)
        try {
            const url = await onImageUpload(file)
            if (url) {
                editor.chain().focus().setImage({ src: url }).run()
            }
        } catch {
            // erro tratado pelo caller (toast); apenas evita quebrar o editor
        } finally {
            setUploadingImage(false)
        }
    }

    const insertTable = () => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
    }

    return (
        <div className="border border-base-300 rounded-lg overflow-hidden bg-base-100">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-1 p-2 bg-base-200 border-b border-base-300">
                {/* Text formatting */}
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive('bold') ? 'btn-active' : ''
                        }`}
                    title="Negrito (Ctrl+B)"
                >
                    <MdFormatBold />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive('italic') ? 'btn-active' : ''
                        }`}
                    title="Itálico (Ctrl+I)"
                >
                    <MdFormatItalic />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive('underline') ? 'btn-active' : ''
                        }`}
                    title="Sublinhado (Ctrl+U)"
                >
                    <MdFormatUnderlined />
                </button>

                <div className="divider divider-horizontal mx-0"></div>

                {/* Lists */}
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive('bulletList') ? 'btn-active' : ''
                        }`}
                    title="Lista com marcadores"
                >
                    <MdFormatListBulleted />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive('orderedList') ? 'btn-active' : ''
                        }`}
                    title="Lista numerada"
                >
                    <MdFormatListNumbered />
                </button>

                <div className="divider divider-horizontal mx-0"></div>

                {/* Alignment */}
                <button
                    type="button"
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive({ textAlign: 'left' }) ? 'btn-active' : ''
                        }`}
                    title="Alinhar à esquerda"
                >
                    <MdFormatAlignLeft />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive({ textAlign: 'center' }) ? 'btn-active' : ''
                        }`}
                    title="Alinhar ao centro"
                >
                    <MdFormatAlignCenter />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive({ textAlign: 'right' }) ? 'btn-active' : ''
                        }`}
                    title="Alinhar à direita"
                >
                    <MdFormatAlignRight />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                    className={`btn btn-sm btn-ghost ${editor.isActive({ textAlign: 'justify' }) ? 'btn-active' : ''
                        }`}
                    title="Justificar"
                >
                    <MdFormatAlignJustify />
                </button>

                <div className="divider divider-horizontal mx-0"></div>

                {/* Insert */}
                <button
                    type="button"
                    onClick={addLink}
                    className={`btn btn-sm btn-ghost ${editor.isActive('link') ? 'btn-active' : ''
                        }`}
                    title="Inserir link"
                >
                    <MdLink />
                </button>
                <button
                    type="button"
                    onClick={addImage}
                    disabled={uploadingImage}
                    className="btn btn-sm btn-ghost"
                    title={onImageUpload ? 'Enviar imagem' : 'Inserir imagem'}
                >
                    {uploadingImage ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        <MdImage />
                    )}
                </button>
                {onImageUpload && (
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageFileChange}
                    />
                )}
                <button
                    type="button"
                    onClick={insertTable}
                    className="btn btn-sm btn-ghost"
                    title="Inserir tabela"
                >
                    <MdTableChart />
                </button>

                <div className="divider divider-horizontal mx-0"></div>

                {/* Actions */}
                <button
                    type="button"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    className="btn btn-sm btn-ghost"
                    title="Desfazer (Ctrl+Z)"
                >
                    <MdUndo />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    className="btn btn-sm btn-ghost"
                    title="Refazer (Ctrl+Y)"
                >
                    <MdRedo />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                    className="btn btn-sm btn-ghost"
                    title="Limpar formatação"
                >
                    <MdFormatClear />
                </button>
            </div>

            {/* Variáveis — inseridas onde o cursor está. */}
            {variables && variables.length > 0 && (
                <div className="flex flex-wrap items-center gap-1 px-3 py-2 bg-base-200/60 border-b border-base-300">
                    <span className="text-xs font-medium text-base-content/60 mr-1">
                        Inserir:
                    </span>
                    {variables.map(variable => (
                        <button
                            key={variable.key}
                            type="button"
                            onClick={() =>
                                editor
                                    .chain()
                                    .focus()
                                    .insertContent(`{{${variable.key}}}`)
                                    .run()
                            }
                            className="btn btn-xs btn-ghost border border-base-300 font-normal"
                            title={variable.hint}
                        >
                            {variable.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Editor */}
            <div
                className="overflow-y-auto bg-base-100 [&_.ProseMirror]:min-h-[var(--editor-min-h)]"
                style={{ minHeight, '--editor-min-h': minHeight } as React.CSSProperties}
            >
                <EditorContent editor={editor} />
            </div>

            {/* Footer with character count */}
            <div className="flex justify-end px-3 py-2 bg-base-200 border-t border-base-300 text-xs text-base-content/60">
                {editor.storage.characterCount?.characters() || 0} caracteres
            </div>
        </div>
    )
}
